import React, { forwardRef, ForwardRefRenderFunction, useRef } from "react";
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { ChatFormProps } from "models/propTypes/commentViewPropTypes";
//#endregion
//#region components
import { Comments, CommentRating, useScrollShadows, MinimizedTitledContainerHeader } from "components/common";
import { ChatAnswerFormContainer } from "components/common/comments";
import { Divider, Grid } from "@mui/material";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { LocalizationHelperInstance } from "utils/helpers";
import { TranslationKeys } from "translations";

const ChatView: ForwardRefRenderFunction<FormHandles, ChatFormProps> = (props, formRef) => {
  const {
    parentId,
    comments,
    getImageContent,
    onFileClick,
    viewCountIncreaseBy,
    defaultViewCount,
    viewCountIncreaseButtonText,
    onSubmitFeedback,
    feedbackWasSubmitted,
    titleContainerHeader,
    titleContainerSubHeader,
    isReadonly,
    hideBottomDivider,
  } = props;

  const scrollViewBottomRef = useRef<HTMLInputElement>(null);
  const ratingComponentRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const shadowTopRef = useRef<HTMLDivElement>(null);
  const shadowBottomRef = useRef<HTMLDivElement>(null);

  useScrollShadows(contentRef, wrapperRef, shadowTopRef, shadowBottomRef, parentId);

  const minNumberOfCommentsToShowRating = 2;
  const sufficientCommentsToShowRating = (comments?.length ?? 0) >= minNumberOfCommentsToShowRating;
  const lastComment = comments?.[comments.length - 1];
  const showRating = sufficientCommentsToShowRating && lastComment?.isWint && props.showRatingComponent;
  const showTitleContainer = titleContainerHeader || titleContainerSubHeader;

  const onFormValidationError = () => {
    ratingComponentRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      {showTitleContainer ? (
        <MinimizedTitledContainerHeader header={titleContainerHeader} subHeader={titleContainerSubHeader} />
      ) : null}
      <Grid
        container
        className={cssExports.commentsWithFileUploader}
        spacing={2}
        marginTop={showTitleContainer ? { xs: 2, lg: 3 } : { xs: -3, lg: -4 }}
        direction="column"
      >
        <Grid className={cssExports.shadowWrapper} ref={wrapperRef}>
          <div
            className={clsx(
              cssExports.shadow,
              showTitleContainer ? cssExports.shadowTopDark : cssExports.shadowTopLight
            )}
            ref={shadowTopRef}
          />
          <Grid container className={cssExports.shadowContent} ref={contentRef} direction="column">
            {showTitleContainer && <Divider className={clsx(cssExports.dividerGray, cssExports.dividerGrayTop)} />}
            <Grid container item marginTop={4} marginBottom={4} gap={4} justifyContent="center">
              {comments?.length ? (
                <Grid container spacing={2}>
                  <Comments
                    parentId={`CommentsWithFileUploader-${parentId}`}
                    viewCountIncreaseBy={viewCountIncreaseBy ?? 2}
                    defaultViewCount={defaultViewCount}
                    viewCountIncreaseButtonText={viewCountIncreaseButtonText}
                    comments={comments}
                    onFileClick={onFileClick}
                    getImageContent={getImageContent}
                    oldestFirst
                    showAllComments={!defaultViewCount}
                  />
                </Grid>
              ) : null}
              {showRating ? (
                <Grid item ref={ratingComponentRef}>
                  <CommentRating
                    targetId={lastComment.id}
                    onSubmitFeedback={onSubmitFeedback}
                    feedbackWasSubmitted={feedbackWasSubmitted}
                  />
                </Grid>
              ) : null}
            </Grid>
            <Grid item ref={scrollViewBottomRef} />
            {!hideBottomDivider && <Divider className={clsx(cssExports.dividerGray, cssExports.dividerGrayBottom)} />}
          </Grid>
          <div className={clsx(cssExports.shadow, cssExports.shadowBottom)} ref={shadowBottomRef} />
        </Grid>
        <Grid container item paddingTop={-4} marginTop={1}>
          {isReadonly ? (
            <Grid className={clsx(cssExports.readonlyMessage)}>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.ThisMessageIsReadOnly)}
            </Grid>
          ) : (
            <ChatAnswerFormContainer ref={formRef} {...props} onFormValidationError={onFormValidationError} />
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default forwardRef(ChatView);
